import { CheckIcon } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { TaskView } from '@/lib/api/generated/model';
import { useBoardMove, useBoardSteps } from '../boardStepsContext';

/**
 * "Move to…" from a card's menu on a phone (spec 262): the board's steps as a
 * list, the one the card is on marked and not offered.
 *
 * Dragging works on a phone now, but a drag across lanes wider than the screen
 * asks a thumb to hold a card while the board scrolls under it. This is the
 * way that needs no aim: the same move a drop makes, with its checks and its
 * Undo.
 */
export function MoveToSheet({
  task,
  open,
  onOpenChange,
}: {
  task: TaskView;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const steps = useBoardSteps();
  const move = useBoardMove();
  if (!move) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Move to…</DialogTitle>
          <DialogDescription>{task.title}</DialogDescription>
        </DialogHeader>
        <ul className="-mx-2 flex flex-col">
          {steps.map((step) => {
            const here = step.stepId === task.position;
            return (
              <li key={step.stepId}>
                <button
                  type="button"
                  disabled={here}
                  aria-current={here ? 'step' : undefined}
                  onClick={() => {
                    onOpenChange(false);
                    move(task, step.stepId);
                  }}
                  className="hover:bg-accent focus-visible:ring-ring flex min-h-11 w-full items-center justify-between gap-3 rounded-md px-2 text-left text-base focus-visible:ring-3 focus-visible:outline-none disabled:cursor-default disabled:opacity-60"
                >
                  <span className="truncate">{step.label}</span>
                  {here ? (
                    <span className="text-muted-foreground flex shrink-0 items-center gap-1 text-sm">
                      <CheckIcon aria-hidden="true" className="size-4" />
                      Here now
                    </span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
